import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { filter, isArray, omit } from 'lodash-es';
import dayjs from 'dayjs';

import { getActiveFilters } from '@/utils/filter';
import { isFilterType } from '@/utils/filter/isFilterType';
import { FILTER_CONFIG, FilterType, FilterValue } from '@/models';
import { syncCachedCountryCodes, writeCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { FilterState } from '@/types/filter';
import { Period } from '@/types/date';
import useSessionStorage, { SessionStorageKey } from '@/hooks/useSessionStorage';
import FiltersContext, { AvailableValuesByFilter, FiltersContextType, OptionPathsByFilter } from './FiltersContext';

const isHydratedPeriod = (value: FilterValue): value is Period => {
  if (typeof value !== 'object' || value === null) return false;
  if (!('beginn' in value && 'end' in value && 'period' in value)) return false;
  return dayjs.isDayjs(value.beginn) && dayjs.isDayjs(value.end);
};

const isRawPeriod = (value: unknown): value is { beginn: string; end: string; period: Period['period'] } => {
  if (typeof value !== 'object' || value === null) return false;
  if (!('beginn' in value && 'end' in value && 'period' in value)) return false;
  return typeof value.beginn === 'string' && typeof value.end === 'string' && typeof value.period === 'string';
};

const rehydratePeriod = (value: FilterValue): Period | undefined => {
  if (isHydratedPeriod(value)) return value;
  if (!isRawPeriod(value)) return undefined;
  const beginn = dayjs(value.beginn);
  const end = dayjs(value.end);
  if (!beginn.isValid() || !end.isValid()) return undefined;
  return { beginn, end, period: value.period };
};

const syncCountryFilterCache = (countryValue: unknown): void => {
  if (!isArray(countryValue)) {
    writeCachedCountryCodes([]);
    return;
  }

  const selectedCountryValues = countryValue.filter(
    (selectedValue): selectedValue is string => typeof selectedValue === 'string',
  );

  syncCachedCountryCodes(selectedCountryValues);
};

export const rehydrateFilterState = (state: FilterState, defaultState: FilterState): FilterState => {
  const rehydrated: FilterState = { ...defaultState, ...state };
  const DATE_FILTER_KEYS: FilterType[] = [FilterType.DATE_RANGE, FilterType.COMPARISON_PERIOD];
  DATE_FILTER_KEYS.forEach(key => {
    if (rehydrated[key] != null) {
      const period = rehydratePeriod(rehydrated[key]);
      rehydrated[key] = period ?? defaultState[key];
    }
  });

  if (rehydrated[FilterType.SINGLE_DATE] != null) {
    const raw = rehydrated[FilterType.SINGLE_DATE];
    const parsed = new Date(String(raw));
    rehydrated[FilterType.SINGLE_DATE] = isNaN(parsed.getTime()) ? defaultState[FilterType.SINGLE_DATE] : parsed;
  }

  return rehydrated;
};

export const getDefaultFiltersState = (customFilterConfig: readonly FilterType[]): FilterState => {
  const state: FilterState = {};
  customFilterConfig?.forEach(filterType => {
    state[filterType] = FILTER_CONFIG[filterType].defaultValue;
  });
  return state;
};

interface FiltersProviderProps {
  children: React.ReactNode;
  customFilterConfig?: readonly FilterType[];
  omitFilters?: readonly FilterType[];
  persist?: boolean;
  initialFilterState?: FilterState;
}

interface FiltersProviderInnerProps {
  children: React.ReactNode;
  filterState: FilterState;
  setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
  removeFilterState: () => void;
  defaultFiltersState: FilterState;
  filterOverlayVisible: boolean;
  setFilterOverlayVisible: (visible: boolean) => void;
  omitFilters?: readonly FilterType[];
  customFilterConfig?: readonly FilterType[];
}

const FiltersProviderInner = ({
  children,
  filterState,
  setFilterState,
  removeFilterState,
  defaultFiltersState,
  filterOverlayVisible,
  setFilterOverlayVisible,
  omitFilters,
  customFilterConfig,
}: FiltersProviderInnerProps) => {
  const { filterState: parentFilterState } = useContext(FiltersContext) || {};

  const [availableValuesByFilter, setAvailableValuesByFilter] = useState<AvailableValuesByFilter>({});

  const registerAvailableValues = useCallback((filterKey: FilterType, values: Set<string>) => {
    setAvailableValuesByFilter(prev => {
      const existing = prev[filterKey];
      if (
        existing &&
        existing.size === values.size &&
        [...values].every(availableValue => existing.has(availableValue))
      ) {
        return prev;
      }
      return { ...prev, [filterKey]: values };
    });
  }, []);

  // Persisted, unlike the availability registry: hierarchical filters read these
  // paths in `applyFilter`, and on a fresh page load the option query that would
  // produce them never runs (it lives in the filters drawer). Without them the
  // rebuilt query would silently use different semantics than the applied one.
  const { value: optionPathsByFilter, setValue: setOptionPathsByFilter } = useSessionStorage<OptionPathsByFilter>(
    SessionStorageKey.GLOBAL_FILTER_OPTION_PATHS,
    {},
  );

  // Filters compute their paths from the drawer's live form value, which is a
  // draft until Apply. Staging keeps that out of the persisted registry (and out
  // of every query it re-keys) so the registry only ever describes the applied
  // filterState. Cancel and close discard the stage instead of committing it.
  const stagedOptionPaths = useRef<OptionPathsByFilter>({});

  const stageOptionPaths = useCallback((filterKey: FilterType, paths: string[]) => {
    stagedOptionPaths.current[filterKey] = paths;
  }, []);

  const clearStagedOptionPaths = useCallback(() => {
    stagedOptionPaths.current = {};
  }, []);

  const commitOptionPaths = useCallback(() => {
    const staged = stagedOptionPaths.current;
    stagedOptionPaths.current = {};

    const stagedEntries = Object.entries(staged).filter(
      (stagedEntry): stagedEntry is [FilterType, string[]] =>
        isFilterType(stagedEntry[0]) && stagedEntry[1] !== undefined,
    );
    if (stagedEntries.length === 0) return;

    setOptionPathsByFilter(prev => {
      const next: OptionPathsByFilter = { ...prev };
      let hasChange = false;

      for (const [filterKey, paths] of stagedEntries) {
        const existing = next[filterKey];
        if (!existing || existing.length !== paths.length || existing.some((path, index) => path !== paths[index])) {
          next[filterKey] = paths;
          hasChange = true;
        }
      }

      return hasChange ? next : prev;
    });
  }, [setOptionPathsByFilter]);

  const replaceOptionPaths = useCallback(
    (paths: OptionPathsByFilter) => setOptionPathsByFilter(paths),
    [setOptionPathsByFilter],
  );

  const handleApplyFilter = useCallback(
    (filtersToApply: FilterState): void => {
      if (Object.hasOwn(filtersToApply, FilterType.COUNTRY)) {
        syncCountryFilterCache(filtersToApply[FilterType.COUNTRY]);
      }

      setFilterState((prevState: FilterState) => ({ ...prevState, ...filtersToApply }));
    },
    [setFilterState],
  );

  const replaceFilters = useCallback(
    (nextFilters: FilterState): void => {
      const replacement = { ...defaultFiltersState, ...nextFilters };
      syncCountryFilterCache(replacement[FilterType.COUNTRY]);
      setFilterState(replacement);
      sessionStorage.removeItem('filter_draft');
    },
    [defaultFiltersState, setFilterState],
  );

  const handleClearAllFilters = useCallback(() => {
    writeCachedCountryCodes([]);
    removeFilterState();
    // Clear the option-path registry too: the hierarchical filters are being
    // reset, so their saved paths must not linger and be read into the next
    // selection's global query.
    setOptionPathsByFilter({});
    sessionStorage.removeItem('filter_draft');
  }, [removeFilterState, setOptionPathsByFilter]);

  const handleDeleteActiveFilter = useCallback(
    (filterKey: FilterType, value: FilterValue) => {
      const currentFilterValue = filterState[filterKey];
      const nextFilterValue = isArray(currentFilterValue)
        ? filter(currentFilterValue, selectedValue => selectedValue !== value)
        : defaultFiltersState[filterKey];

      if (filterKey === FilterType.COUNTRY) {
        syncCountryFilterCache(nextFilterValue);
      }

      setFilterState((prevState: FilterState) => {
        return {
          ...prevState,
          [filterKey]: nextFilterValue,
        };
      });
      sessionStorage.removeItem('filter_draft');
    },
    [defaultFiltersState, filterState, setFilterState],
  );

  const context = useMemo(() => {
    const mergedFilterState = parentFilterState
      ? { ...omit(parentFilterState, omitFilters), ...filterState }
      : filterState;

    const activeFilters = getActiveFilters(mergedFilterState, defaultFiltersState);
    return {
      filterState: mergedFilterState,
      defaultFiltersState,
      filterOverlayVisible,
      setFilterOverlayVisible,
      handleApplyFilter,
      replaceFilters,
      handleClearAllFilters,
      handleDeleteActiveFilter,
      activeFilters,
      numberActiveFilterCategories: activeFilters.length,
      filterConfig: customFilterConfig,
      availableValuesByFilter,
      registerAvailableValues,
      optionPathsByFilter,
      stageOptionPaths,
      commitOptionPaths,
      clearStagedOptionPaths,
      replaceOptionPaths,
    };
  }, [
    defaultFiltersState,
    filterState,
    parentFilterState,
    omitFilters,
    filterOverlayVisible,
    setFilterOverlayVisible,
    handleApplyFilter,
    replaceFilters,
    handleClearAllFilters,
    handleDeleteActiveFilter,
    customFilterConfig,
    availableValuesByFilter,
    registerAvailableValues,
    optionPathsByFilter,
    stageOptionPaths,
    commitOptionPaths,
    clearStagedOptionPaths,
    replaceOptionPaths,
  ]);

  return <FiltersContext.Provider value={context}>{children}</FiltersContext.Provider>;
};

const PersistedFiltersProvider = ({
  customFilterConfig,
  omitFilters,
  children,
}: Omit<FiltersProviderProps, 'persist'>) => {
  const defaultFiltersState = useMemo(() => getDefaultFiltersState(customFilterConfig), [customFilterConfig]);
  const [filterOverlayVisible, setFilterOverlayVisible] = useState(false);

  const {
    value: rawFilterState,
    setValue: setFilterState,
    removeValue: removeFilterState,
  } = useSessionStorage<FilterState>(SessionStorageKey.GLOBAL_FILTERS, defaultFiltersState);

  const filterState = useMemo(
    () => rehydrateFilterState(rawFilterState, defaultFiltersState),
    [rawFilterState, defaultFiltersState],
  );

  return (
    <FiltersProviderInner
      filterState={filterState}
      setFilterState={setFilterState}
      removeFilterState={removeFilterState}
      defaultFiltersState={defaultFiltersState}
      filterOverlayVisible={filterOverlayVisible}
      setFilterOverlayVisible={setFilterOverlayVisible}
      omitFilters={omitFilters}
      customFilterConfig={customFilterConfig}
    >
      {children}
    </FiltersProviderInner>
  );
};

const LocalFiltersProvider = ({
  customFilterConfig,
  omitFilters,
  children,
  initialFilterState,
}: Omit<FiltersProviderProps, 'persist'>) => {
  const defaultFiltersState = useMemo(() => getDefaultFiltersState(customFilterConfig), [customFilterConfig]);
  const [filterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>(() => ({
    ...defaultFiltersState,
    ...(initialFilterState ?? {}),
  }));

  const removeFilterState = useCallback(() => {
    setFilterState(defaultFiltersState);
  }, [defaultFiltersState]);

  return (
    <FiltersProviderInner
      filterState={filterState}
      setFilterState={setFilterState}
      removeFilterState={removeFilterState}
      defaultFiltersState={defaultFiltersState}
      filterOverlayVisible={filterOverlayVisible}
      setFilterOverlayVisible={setFilterOverlayVisible}
      omitFilters={omitFilters}
      customFilterConfig={customFilterConfig}
    >
      {children}
    </FiltersProviderInner>
  );
};

export const FiltersProvider = ({ persist = false, ...props }: FiltersProviderProps) => {
  if (persist) return <PersistedFiltersProvider {...props} />;
  return <LocalFiltersProvider {...props} />;
};

export const useFiltersContext = (): FiltersContextType => useContext(FiltersContext);

export default FiltersContext;
